import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ACCESS_TYPE, Prices, TYPE_MARKETING } from '@bytel/bytel-sales';

import { Catalog } from '@catalog/products/catalog';
import { Phone } from '@catalog/products/equipement/complex/phone';
import { Product } from '@catalog/products/product';
import { Edp } from '@catalog/products/subscription/edp';
import { Plan } from '@catalog/products/subscription/plan';
import { Sensation } from '@catalog/products/subscription/plan/premium/sensation';
import { Simo } from '@catalog/products/subscription/plan/simo';
import { Sowo } from '@catalog/products/subscription/plan/sowo';

import { Scheme } from '@checkout/cart/scheme.class';

import { CustomerContextService } from '@context/child/customer-context.service';
import { ProductContext } from '@context/child/product-context.service';
import { ContextService } from '@context/context.service';

import { FUNDING_MODES } from '@app/baseBtl/catalog/products.service';
import { ConfiguratorType } from '@app/baseBtl/catalog/products/interface/configurable';
import {
    ApiFunding,
    ApiProduct,
    ApiProductContainers,
    ApiPromos,
    CurrentPrices,
    Parcours,
    PlanJsonCategories,
    Promotion,
    SchemeAndPrices,
    ShippingDataModel,
    TypesMarketing,
} from '@app/baseBtl/catalog/products/interface/context';
import { DeliveryMode, DeliveryModesParams, TypeParcoursEnums } from '@app/baseBtl/catalog/products/interface/delivery';
import { RenewStorageService } from '@app/catalog/category/renew/renew-storage.service';
import { ProductsService } from '@app/catalog/products.service';
import { SensationPro } from '@app/catalog/products/subscription/plan/pro/sensation';
import { Prompto } from '@app/catalog/products/subscription/plan/prompto';
import { InfoHuaweiModalComponent } from '@app/catalog/products/view/modals/info-huawei-modal/info-huawei-modal.component';
import { CartHelperService } from '@app/checkout/cart/helper/cart-helper.service';
import { RenewContextService } from '@app/context/child/renew-context.service';
import { PromotionsModelWebContainers } from '@app/interfaces/api/promotions-v2.interface';
import { MobileRecoveryService } from '@app/modules/mobile-recovery/mobile-recovery.service';
import { BaseProduct } from '@app/modules/product-wall/services/factory/base-product.class';

import { ConfigurationService } from '@common';
import { ModalRef, ModalService } from '@trilogy';
import { Observable, Subject, of as observableOf, of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

import { STOCK_STATUS, TIME_LAPSE } from '../../baseBtl/catalog/products/Enum';
import { Configurator } from '../../catalog/products/interface/configurable';
import { JsonProduct } from '../../catalog/products/interface/context';
import { EresaService } from '../../catalog/services/eresa.service';
import { WALLS } from '../classes/btl-constants';
import { DateUniversal } from '../classes/date-universal.class';
import { BtlHelperService } from './btl-helper.service';
import { BytelTagService } from './bytel-tag.service';
import { PromotionService } from './promotion.service';

@Injectable({
    providedIn: 'root',
})
export class ViewService {
    public parentProduct: JsonProduct;
    public keptPlan: JsonProduct;

    public configuratorSubject: Subject<Configurator> = new Subject<Configurator>();
    public configuratorsSubject: Subject<Configurator[]> = new Subject<Configurator[]>();
    public productSubject: Subject<JsonProduct> = new Subject<JsonProduct>();
    public keptPlanSubject: Subject<JsonProduct> = new Subject<JsonProduct>();
    public canShowClickAndCollectSubject: Subject<boolean> = new Subject<boolean>();
    public canApplyWithoutTaxSignal = signal(false);
    public schemeAndPricesSubject: Subject<SchemeAndPrices> = new Subject<SchemeAndPrices>();
    public fundingsSubject: Subject<ApiFunding[]> = new Subject<ApiFunding[]>();
    public apiPlanSubject: Subject<ApiProduct> = new Subject<ApiProduct>();
    public apiProduct: ApiProduct;
    public minPlanDataFromApiSubject: Subject<Parcours> = new Subject<Parcours>();
    public keptPlanSubjectFromConfigurator: Subject<JsonProduct> = new Subject<JsonProduct>();
    public loadedDataFromApi: ApiProductContainers;
    // permet de distinguer le sans forfait voulu par l'utilisateur
    // et le sans forfait première visite et évite de forcer un forfait
    private withoutPlan = false;

    public promotionsApi: PromotionsModelWebContainers;

    private product: JsonProduct;
    private scheme: Scheme;
    private prices: CurrentPrices;
    private instantDiscount: number = 0;
    public stockInfos = new Map();
    public tagValues = {
        pageCategorie: 'Fiche_produit_telephone',
        page: '',
        parcours: 'Acquisition',
        activiteB2BouB2C: 'B2C',
        produits: [],
        produit: {},
        typeUtilisateur: '',
    };

    private TAG_CATEGORIES = {
        phone_simple: 'Telephone',
        accessory: 'Accessoire',
        box: 'Hotspot_tablette',
    };

    public fundings: ApiFunding[] = [];
    public selectedFunding: ApiFunding;
    public apiPlan: ApiProduct;

    private productStockStatus: string = '';
    private authorizedDeliveryModes = ['LA_POSTE', 'CHRONOPOST', 'LIVRAISON_DIRECTE'];
    private excludedDeliveryModes = ['CLICK_COLLECT'];
    private salesPercent: number;
    private renewMinPlanData: JsonProduct;

    protected http: HttpClient = inject(HttpClient);
    protected eresaService: EresaService = inject(EresaService);
    protected config: ConfigurationService = inject(ConfigurationService);
    protected tagService: BytelTagService = inject(BytelTagService);
    protected mobileRecoveryService: MobileRecoveryService = inject(MobileRecoveryService);
    protected productService: ProductsService = inject(ProductsService);
    @Inject(DOCUMENT) protected document: Document = inject(DOCUMENT);
    protected customerContextService: CustomerContextService = inject(CustomerContextService);
    protected renewContextService: RenewContextService = inject(RenewContextService);
    protected contextService: ContextService = inject(ContextService);
    protected baseProductService: BaseProduct = inject(BaseProduct);
    protected btlHelper: BtlHelperService = inject(BtlHelperService);
    protected route: ActivatedRoute = inject(ActivatedRoute);
    protected router: Router = inject(Router);
    protected cartHelper: CartHelperService = inject(CartHelperService);
    protected promotionService: PromotionService = inject(PromotionService);
    private storage = inject(RenewStorageService);

    constructor() {
        this.initStockInfos();
        this.productSubject.subscribe((product) => {
            this.product = product;
            this.generateScheme(product);
            this.sendTagsWithNewProduct(product);
        });

        this.keptPlanSubjectFromConfigurator.subscribe((plan: JsonProduct) => {
            if (plan?.entity_id !== this.keptPlan?.entity_id) {
                this.refreshScheme(plan, false);
            }
        });
    }

    private generateScheme(product: JsonProduct): void {
        if (this.baseProductService.wallType === 'bon-plan') {
            this.baseProductService.cardType = this.baseProductService.getCartTypeFromProduct(product);
        }

        if (this.baseProductService.isRenewal) {
            this.contextService.setContext(this.renewContextService);
        }

        this.scheme = new Scheme(new ProductContext(this.contextService));
        let firstStep = of({});
        const planTypes = this.getPlanTypeToDisplay();
        if (planTypes?.[0]) {
            firstStep = this.productService.getProductsByCategories(planTypes).pipe(
                tap((resPlans: PlanJsonCategories) => {
                    const plans: JsonProduct[] = resPlans.categories[0].products;
                    const planByUrl: JsonProduct = this.baseProductService.getUrlPlan(plans, this.document);
                    const plan = this.getCurrentPlan(planByUrl, plans);
                    this.keptPlan = plan;

                    if (!this.withoutPlan) {
                        this.keptPlanSubject.next(plan);
                    }

                    plan.is_pro = this.baseProductService.canCountOnlyPlanPro
                        ? this.cartHelper.isPro()
                        : this.isWithoutTax();
                    if (!!plan.sku) {
                        this.scheme.add(Catalog.getInstance(plan, this.scheme.productContext));
                    }
                }),
            );
        } else if (this.baseProductService.isRenewal) {
            this.keptPlan = this.cartHelper.getPlanInQuote() || this.renewContextService.plan;
            this.scheme.add(Catalog.getInstance(this.keptPlan, this.scheme.productContext));
            firstStep = of([]);
        }

        firstStep.subscribe(() => {
            this.scheme.add(Catalog.getInstance(product, this.scheme.productContext));

            const device: Product = this.scheme.getProductByType(Phone);
            if (device instanceof Phone) {
                const sensationPlans = [Sensation.PLAN_TYPE, SensationPro.PLAN_TYPE];
                const keptPlanType = this.baseProductService.getPlanType();
                if (sensationPlans.includes(keptPlanType) && device.isEdpAppliable(this.scheme, keptPlanType)) {
                    this.scheme.add(Catalog.getInstance(Edp.defaultData, this.scheme.productContext));
                }
            }

            this.scheme.is_pro = this.isWithoutTax();
            this.baseProductService.isWithoutTax = this.isWithoutTax();
            this.baseProductService.numberFix = this.isWithoutTax() ? 2 : 0;
            this.spreadScheme(this.scheme, true);
        });
    }

    private refreshScheme(planItem: JsonProduct, isLoadApiNeeded: boolean = true): void {
        const sowoInCart = planItem?.type_id === Sowo.PLAN_TYPE ? planItem : null;
        let planType = Sensation.PLAN_TYPE;
        this.withoutPlan = !planItem;

        if (sowoInCart) {
            const hasObligation = (sowoInCart.obligation_id || sowoInCart.obligation) !== 'none';
            planType = hasObligation ? Simo.PLAN_TYPE : Sowo.PLAN_TYPE;
        }
        this.baseProductService.setPlanType(planType);
        this.scheme = new Scheme(new ProductContext(this.contextService));
        this.keptPlan = planItem;

        if (planItem) {
            planItem.is_pro = this.baseProductService.canCountOnlyPlanPro
                ? this.cartHelper.isPro()
                : this.isWithoutTax();
            this.scheme.add(Catalog.getInstance(planItem, this.scheme.productContext), 1, true);
        } else {
            const planToDelete = this.scheme.getProductByType(Plan);

            if (planToDelete) {
                this.scheme.remove(planToDelete.uniqueId);
                this.spreadScheme(this.scheme, isLoadApiNeeded);
            }
        }
        this.scheme.is_pro = this.isWithoutTax();
        this.scheme.add(Catalog.getInstance(this.product, this.scheme.productContext));

        this.prepareScheme(isLoadApiNeeded);
    }

    private prepareScheme(isLoadApiNeeded: boolean): void {
        const device: Product = this.scheme.getProductByType(Phone);

        if (device instanceof Phone) {
            const sensationPlans = [Sensation.PLAN_TYPE, SensationPro.PLAN_TYPE];
            const keptPlanType = this.baseProductService.getPlanType();
            if (sensationPlans.includes(keptPlanType) && device.isEdpAppliable(this.scheme, keptPlanType)) {
                this.scheme.add(Catalog.getInstance(Edp.defaultData, this.scheme.productContext));
            }
        }

        this.spreadScheme(this.scheme, isLoadApiNeeded);
    }

    public spreadScheme(scheme: Scheme, isLoadApiNeeded: boolean): void {
        const plan = scheme.getProductByType(Plan) as Plan;
        const product = scheme.getProductByType(this.baseProductService.productType);
        const productGencodes = [];
        if (product) {
            productGencodes.push(product.gencode);
        }
        if (plan) {
            productGencodes.push(plan.gencode);
        }

        const contractId = this.renewContextService.contractId;

        const minPlanData = this.renewMinPlanData;
        const productGenCodes = [product?.gencode];
        if (plan) {
            if (isLoadApiNeeded || !this.loadedDataFromApi) {
                // load from SAPIC data with plans
                this.loadDataFromApiWithPlans(productGenCodes, contractId, plan, scheme, minPlanData);
            } else {
                // get Data from existing values
                this.useExistingDataPlans(plan, minPlanData, scheme);
            }
        } else {
            // for device without plans
            this.loadStandAloneDevices(productGenCodes, contractId, scheme, minPlanData);
        }
    }

    private useExistingDataPlans(plan: Plan, minPlanData: JsonProduct, scheme: Scheme): void {
        const panierSimuleProduits = this.loadedDataFromApi?.[plan?.gencode]?.panierSimule?.parcours[0]?.produits;
        if (minPlanData?.gencode) {
            const parcoursMinPlan =
                this.loadedDataFromApi?.[minPlanData?.gencode]?.[plan?.gencode]?.panierSimule?.parcours[0]?.produits;
            this.minPlanDataFromApiSubject.next(parcoursMinPlan);
        }
        this.handleLoadedData(panierSimuleProduits, scheme);
    }

    private loadDataFromApiWithPlans(
        productGenCodes: string[],
        contractId: string,
        plan: Plan,
        scheme: Scheme,
        minPlanData: JsonProduct,
    ): void {
        const planTypes = this.getPlanTypeToDisplay().join(',');
        if (planTypes.length !== 0 && !this.baseProductService.isRenewal) {
            this.productService
                .getPromotionFromProduitsContextualises(productGenCodes, planTypes, this.isWithoutTax(), contractId)
                .subscribe((res) => {
                    const panierSimuleProduits = res?.[plan?.gencode]?.panierSimule?.parcours[0]?.produits;
                    if (minPlanData?.gencode) {
                        const parcoursMinPlan =
                            res?.[minPlanData?.gencode]?.[plan?.gencode]?.panierSimule?.parcours[0]?.produits;
                        this.minPlanDataFromApiSubject.next(parcoursMinPlan);
                    }
                    this.loadedDataFromApi = res;
                    if (!!panierSimuleProduits) {
                        this.handleLoadedData(panierSimuleProduits, scheme);
                    } else {
                        // handle case when the selected plan dosen't exist in the response of SAPIC api
                        this.loadDataFromApiByGencodes(productGenCodes, plan, contractId, scheme, minPlanData);
                    }
                });
        } else {
            this.loadDataFromApiByGencodes(productGenCodes, plan, contractId, scheme, minPlanData);
        }
    }

    private loadDataFromApiByGencodes(
        productGenCodes: string[],
        plan: Plan,
        contractId: string,
        scheme: Scheme,
        minPlanData: JsonProduct,
    ) {
        const plans: string[] = [plan?.gencode];
        if (minPlanData?.gencode && minPlanData?.gencode !== plan?.gencode) {
            plans.push(minPlanData.gencode);
        }
        this.productService
            .getProductWithPrices(
                productGenCodes,
                plans.join(','),
                FUNDING_MODES.MIN_ONE_OFF,
                contractId,
                this.isWithoutTax(),
            )
            .subscribe((dataOfSelectedPlan) => {
                const dataPlanParsed = dataOfSelectedPlan?.produits?.reduce(
                    (acc, el) => ({
                        ...acc,
                        [el.gencode]: el,
                    }),
                    {},
                );
                this.loadedDataFromApi = { ...dataPlanParsed, ...this.loadedDataFromApi };
                const planPanierSimuleProduits = dataPlanParsed?.[plan?.gencode]?.panierSimule?.parcours[0]?.produits;
                if (minPlanData?.gencode) {
                    const parcoursMinPlan = dataPlanParsed?.[minPlanData?.gencode]?.panierSimule?.parcours[0];
                    this.minPlanDataFromApiSubject.next(parcoursMinPlan);
                }
                this.handleLoadedData(planPanierSimuleProduits, scheme);
            });
    }

    private loadStandAloneDevices(
        productGenCodes: string[],
        contractId: string,
        scheme: Scheme,
        minPlanData: JsonProduct,
    ): void {
        this.productService
            .getProductWithPrices(productGenCodes, '', FUNDING_MODES.MIN_ONE_OFF, contractId, this.isWithoutTax())
            .subscribe((res) => {
                const panierSimuleProduits = res?.produits[0]?.panierSimule?.parcours[0]?.produits;
                if (minPlanData?.gencode) {
                    const parcoursMinPlan = this.findMinPlanInResponse(minPlanData?.gencode, res?.produits);
                    this.minPlanDataFromApiSubject.next(parcoursMinPlan);
                }
                this.handleLoadedData(panierSimuleProduits, scheme);
            });
    }

    private handleLoadedData(panierSimuleProduits: ApiProduct[], scheme: Scheme): void {
        this.apiProduct = this.getApiProduct(panierSimuleProduits);
        this.apiPlan = this.getApiPlan(panierSimuleProduits);
        this.apiPlanSubject.next(this.apiPlan);
        if (this.baseProductService.isRenewal) {
            this.updateGalliumPrices(this.apiPlan);
        }
        const promos = this.apiProduct?.promotions || [];
        const urlParams = new URLSearchParams(window.location.search);
        const urlFunding = urlParams.get('financement');
        const forcedFunding = urlFunding !== null;

        const odr = this.getApiOdr();
        this.fundings = this.apiProduct?.financements
            .map((funding: ApiFunding) => {
                const prePriceWithOdr = funding.apportInitial - odr;
                funding.apportInitialOdr = prePriceWithOdr > 1 ? prePriceWithOdr : 1;
                funding.apportInitialOdrHT = Number(this.btlHelper.round(funding.apportInitialOdr, 2));

                funding.label = this.getFundingLabels(funding);

                if (forcedFunding) {
                    funding.prefere = funding.type.includes(urlFunding.toUpperCase());
                }

                return funding;
            })
            .filter((funding: ApiFunding) => !funding.type.includes('COFIDIS'));
        this.fundings =
            this.filterFundings(this.fundings).sort(
                (a: ApiFunding, b: ApiFunding) => this.getFundingOrder(a.type) - this.getFundingOrder(b.type),
            ) || [];
        this.setSelectedFunding();
        if (this.baseProductService.wallType === WALLS.TYPE.PHONE) {
            sessionStorage.setItem('funding_key', this.selectedFunding?.type);
        }

        this.setPromosAPI(promos);
        if (this.baseProductService.canShowConfigurators) {
            this.initConfigurators();
        }
        this.setPromos();

        this.fundingsSubject.next(this.fundings);
        this.schemeAndPricesSubject.next({ scheme, prices: this.prices });
    }

    private getApiProduct(panierSimuleProduits: ApiProduct[]): ApiProduct {
        return panierSimuleProduits.filter(
            (product) =>
                product.type.includes('phone') || product.type.includes('accessory') || product.type.includes('box'),
        )[0];
    }

    private getApiPlan(panierSimuleProduits: ApiProduct[]): ApiProduct {
        return panierSimuleProduits.filter(
            (produit) => produit.type.includes('plan') || produit.type.includes('faim'),
        )[0];
    }

    private setPromosAPI(promos: Promotion[]): void {
        this.instantDiscount = promos.reduce(
            (acc: number, promo: Promotion) => (promo.type !== 'ODR' ? promo.valeur + acc : acc),
            0,
        );
        this.salesPercent = promos.reduce(
            (acc: number, promo: Promotion) =>
                !!promo?.proprietesSupplementaires?.typesMarketing?.includes(TypesMarketing.SOLDES)
                    ? promo.valeur + acc
                    : acc,
            0,
        );
    }

    private getApiOdr() {
        return this.apiProduct?.promotions.find((promo: ApiPromos) => promo.type === 'ODR')?.reduction || 0;
    }

    private filterFundings(fundings: ApiFunding[]): ApiFunding[] {
        const younitedKeys = ['YOUNITED_X24', 'YOUNITED_X12', 'YOUNITED_X36'];
        const younitedFoundings: ApiFunding[] = [];
        const otherFoundings: ApiFunding[] = [];

        fundings.forEach((funding: ApiFunding) => {
            if (funding.type.includes('YOUNITED')) {
                younitedFoundings.push(funding);
            } else {
                otherFoundings.push(funding);
            }
        });
        const younitedFoundingPrefered = younitedFoundings.find((funding: ApiFunding) => funding.prefere);
        if (!younitedFoundingPrefered) {
            younitedFoundings.sort(
                (a: ApiFunding, b: ApiFunding) => younitedKeys.indexOf(a.type) - younitedKeys.indexOf(b.type),
            );
        }
        const younitedSelected = younitedFoundingPrefered || younitedFoundings[0];
        return younitedSelected ? [...otherFoundings, younitedSelected] : otherFoundings;
    }

    private getFundingOrder(type) {
        switch (type) {
            case 'EDP':
                return 1;
            case 'COMPTANT':
                return 2;
            default:
                return 0;
        }
    }

    private getFundingLabels(funding: ApiFunding) {
        switch (funding.type) {
            case 'COMPTANT':
                return 'Comptant';
            case 'EDP':
                return 'Facilité de paiement sans frais';
            default: {
                const fundingLabel = funding.tAEG === 0 ? `${funding.tAEG}%` : '';
                return `Financement smartphone ${fundingLabel}`;
            }
        }
    }

    private setSelectedFunding(forcedValue?: string | boolean) {
        const presetValue = forcedValue;
        this.selectedFunding =
            this.fundings.find((funding: ApiFunding) =>
                presetValue ? funding.type === presetValue : !!funding.prefere,
            ) || this.fundings[0];
    }

    public setPromos() {
        let planPrices: Prices;

        const odr = this.apiProduct?.promotions?.find((promo: ApiPromos) => promo.type === 'ODR')?.reduction || 0;

        if (this.apiPlan) {
            const configurators: TYPE_MARKETING[] = Array.from(this.scheme.configurators.values())
                .map((value) => value.accessType)
                .filter((value) => !!value);
            planPrices = this.promotionService.getPricesWithPromos(
                this.apiPlan.prix.initial,
                this.apiPlan.promotions,
                configurators,
            );
        }

        this.prices = {
            planPrices,
            odr,
            selectedFunding: this.selectedFunding,
            instantDiscount: this.instantDiscount,
            salesPercent: this.salesPercent,
        };
    }

    public getCurrentPlan(planByUrl: JsonProduct, plans: JsonProduct[]): JsonProduct {
        let plan: JsonProduct;
        const plansInCart = this.cartHelper.getPlan();
        if (!this.withoutPlan) {
            if (planByUrl) {
                plan = planByUrl;
            } else if (this.keptPlan) {
                plan = this.keptPlan;
            } else if (plansInCart && this.baseProductService.wallPlans.includes(plansInCart.type_id)) {
                plan = plansInCart;
            } else {
                plan = this.getDefaultPlan(plans);
            }
        }

        return { ...plan, ...plans[plan?.sku] };
    }

    private getDefaultPlan(plans: JsonProduct[]): JsonProduct {
        const defPlanId = this.config.getData()?.global.catalog.min_plan[this.baseProductService.defaultPlanIdType];
        const plan: JsonProduct = Object.values(plans).find(
            (planToFilter: JsonProduct) => planToFilter.gencode === defPlanId,
        );
        return plan || plans[Object.keys(plans)[0]];
    }

    public setParentProduct(parentProduct: JsonProduct): void {
        this.parentProduct = parentProduct;
    }

    public sendTagsWithNewProduct(product: JsonProduct): void {
        this.tagValues.pageCategorie = `Fiche_produit_${this.TAG_CATEGORIES[product.type_id].toLowerCase()}`;
        this.tagValues.page = `${this.TAG_CATEGORIES[product.type_id]}_${product.name.replace(/ /g, '_')}`;
        this.tagValues.produits = [
            {
                name: product.name,
                id: product.entity_id,
                gencode: product.gencode,
                sku: product.sku,
                price: product.price,
                type_id: product.type_id,
                manufacturer: product.manufacturer,
                ligneProduit_niv2: this.TAG_CATEGORIES[product.type_id],
            },
        ];

        if (product.type_id === 'phone_simple') {
            this.tagValues['pageVariante'] = {
                repriseMobile: `${this.mobileRecoveryService.getBonus() > 0 ? 'avec' : 'sans'}_bonus`,
                repriseMobileBandeau: !product.anim_co,
            };
            if (this.baseProductService.isRenewal) {
                this.tagValues['pageVariante']['forfaitDetenu'] =
                    this.renewContextService.plan?.name?.replace(/ /g, '_') || '';
            }
            this.tagValues['variablesSpecifiques'] = { stock: this.productStockStatus.replace(/\s+/g, '_') };
            this.tagValues.produits[0].color = product.color;
            this.tagValues.produits[0].capacity = product.capacity;
        }

        this.tagService.send({ ...this.tagValues });
    }

    public sendClickAndCollectTag(isModal: boolean = false) {
        const tags = Object.assign(this.tagValues, {
            nomClic: `Retirer_en_boutique_fiche_${isModal ? 'modal' : 'produit'}`,
            categorieClic: 'Click_and_collect',
            actionClic: 'Clic_redirection',
        });
        this.tagService.send(tags);
    }

    public getShippingDate(isRenew = false, gencode?: string): Observable<ShippingDataModel> {
        const customerService = isRenew ? this.renewContextService : this.customerContextService;
        const service = customerService ? customerService.getAddresses() : of([]);

        return service.pipe(
            mergeMap((res) => {
                const codePostal = res[0]?.codePostal || '75000';
                const typeClient = this.isProContext() ? 'PRO' : 'GP';
                const deliveryModeParams: DeliveryModesParams = {
                    typeParcours: TypeParcoursEnums.ACQUISITION,
                    typeClient,
                    gencodes: [gencode],
                    adresse: { codePostal },
                };

                return this.productService.getDeliveryModes(deliveryModeParams);
            }),
            map((deliveryModes) => {
                const modesLivraison = deliveryModes.modesLivraison.filter((modeLivraison) =>
                    this.authorizedDeliveryModes.includes(modeLivraison.gencode),
                );
                const canFreeModesLivraison = deliveryModes.modesLivraison.filter(
                    (modeLivraison) => !this.excludedDeliveryModes.includes(modeLivraison.gencode),
                );
                const closestDeliveryDate: Date = this.closestDeliveryDate(modesLivraison);
                const isFreeShipping = canFreeModesLivraison.filter((livraison) => livraison.prix === 0).length > 0;
                const closestDeliveryUniversalDate: DateUniversal = new DateUniversal(
                    closestDeliveryDate.toDateString(),
                );
                let wording: string;
                switch (closestDeliveryUniversalDate.getTimeLapse()) {
                    case TIME_LAPSE.DAY:
                        wording = 'dès demain';
                        break;
                    case TIME_LAPSE.WEEK:
                        wording = `à partir de ${closestDeliveryUniversalDate.getWeekDay()}`;
                        break;
                    default:
                        wording = `à partir du ${closestDeliveryUniversalDate.toShortSimpleFrench()}`;
                        break;
                }
                return { wording, isFreeShipping };
            }),
        );
    }

    public closestDeliveryDate(modesLivraison: DeliveryMode[]): Date {
        const dates: Date[] = modesLivraison
            .map((modeLivraison) => new Date(modeLivraison.dateLivraisonEstimee))
            .sort((a, b) => a.getTime() - b.getTime());

        return dates[0];
    }

    public initStockInfos() {
        this.stockInfos.set(STOCK_STATUS.OUT_OF_STOCK, {
            icon: 'tri-shopping-cart',
            altIcon: 'tri-times-circle',
            color: 'is-danger',
            label: 'Rupture de stock en ligne',
            cart: 'Voir les produits similaires',
            phoneAlone: '',
            isText: true,
            status: 0,
        });
        this.stockInfos.set(STOCK_STATUS.IN_STOCK, {
            icon: 'tri-shopping-cart',
            altIcon: 'tri-check-circle',
            color: 'is-success',
            label: 'En stock en ligne',
            subLabel: 'Livré',
            altSubLabel: 'livré',
            hasShippingDate: true,
            canShowCnC: true,
            cart: 'Ajouter au panier',
            ctaLabelByou: 'Voir les autres offres',
            phoneAlone: 'Commander',
            status: 1,
        });
        this.stockInfos.set(STOCK_STATUS.SOON_AVAILABLE, {
            icon: 'tri-bell-ringing',
            color: 'is-warning',
            label: 'Bientôt disponible',
            cart: "M'alerter de la disponibilité",
            phoneAlone: '',
            isText: true,
            status: 2,
        });
        this.stockInfos.set(STOCK_STATUS.SOON_OUT_OF_STOCK, {
            icon: 'tri-shopping-cart',
            color: 'is-success',
            label: 'Stock faible en ligne',
            subLabel: 'Livré',
            altSubLabel: 'livré',
            hasShippingDate: true,
            canShowCnC: true,
            cart: 'Ajouter au panier',
            ctaLabelByou: 'Voir les autres offres',
            phoneAlone: 'Commander',
            date: '',
            status: 3,
        });
        this.stockInfos.set(STOCK_STATUS.ONLY_IN_SHOP, {
            icon: 'tri-store',
            color: 'is-danger',
            label: 'Rupture de stock en ligne',
            cart: 'Voir les produits similaires',
            phoneAlone: '',
            isText: true,
            status: 4,
        });
        this.stockInfos.set(STOCK_STATUS.IN_STOCK_FROM, {
            icon: 'tri-shopping-cart',
            color: 'is-warning',
            label: 'Disponible en ligne',
            subLabel: 'Livré',
            altSubLabel: 'livré',
            hasShippingDate: true,
            cart: 'Pré-commander',
            ctaLabelByou: 'Voir les autres offres',
            phoneAlone: 'Pré-commander',
            status: 5,
        });
    }

    public initStock(phone: JsonProduct) {
        if (phone.exclu_web) {
            return observableOf(this.checkStock(false, phone));
        } else {
            return this.eresaService.getStock(phone.gencode).pipe(
                map((eresa: any) => this.checkStock(eresa, phone)),
                catchError(() => {
                    throw this.checkStock(false, phone);
                }),
            );
        }
    }

    public checkStock(eresa, phone: JsonProduct) {
        const stock = { eresa: null, preresa: null };
        let status;

        switch (true) {
            // Pré-commande
            case phone.is_preorder && phone.in_stock:
                status = STOCK_STATUS.IN_STOCK_FROM;
                break;

            // Pas de stock && e-resa => Uniquement boutique
            case !phone.in_stock && eresa.eresa:
                status = STOCK_STATUS.ONLY_IN_SHOP;
                break;

            // Pas de eresa + config BO bientot dispo ==> bientôt disponible
            case !phone.in_stock && !eresa.eresa && phone.available_soon:
                status = STOCK_STATUS.SOON_AVAILABLE;
                break;

            // bientôt Epuise
            case phone.soon_exhausted:
                status = STOCK_STATUS.SOON_OUT_OF_STOCK;
                break;

            // Pas de stock
            case !phone.in_stock:
                status = STOCK_STATUS.OUT_OF_STOCK;
                break;

            default:
                status = STOCK_STATUS.IN_STOCK;
                break;
        }

        const eresaStock = this.stockInfos.get(status);
        eresaStock.status = status;
        eresaStock.active = eresa.eresa;
        this.productStockStatus = eresaStock.label;

        stock.eresa = eresaStock;
        stock.preresa = eresa.preresa;

        return stock;
    }

    public addConfigurator(configurator: Configurator, addEdp?: boolean): void {
        const device: Product = this.scheme.getProductByType(Phone);
        let edp: Edp = this.scheme.getProductByType(Edp) as Edp;

        if (addEdp && !edp && device instanceof Phone) {
            const sensationPlans = [Sensation.PLAN_TYPE, SensationPro.PLAN_TYPE];
            const keptPlanType = this.baseProductService.getPlanType();
            if (sensationPlans.includes(keptPlanType) && device.isEdpAppliable(this.scheme, keptPlanType)) {
                this.scheme.add(Catalog.getInstance(Edp.defaultData, this.scheme.productContext));
            }
        }
        edp = addEdp ? (this.scheme.getProductByType(Edp) as Edp) : null;

        this.scheme.addConfigurators([configurator], edp);

        if (configurator.id === 'EDP') {
            this.setSelectedFunding(configurator.value);
            sessionStorage.setItem('funding_key', this.selectedFunding?.type);
        }

        this.setPromos();
        this.schemeAndPricesSubject.next({ scheme: this.scheme, prices: this.prices });
    }

    public setConfigurators(configurators: Configurator[]): void {
        const device: Product = this.scheme.getProductByType(Phone);
        let edp: Edp = this.scheme.getProductByType(Edp) as Edp;
        const hasEdpConfig = configurators.some(
            (configurator: Configurator) => configurator.id === ConfiguratorType.EDP,
        );

        if (hasEdpConfig && !edp && device instanceof Phone) {
            const sensationPlans = [Sensation.PLAN_TYPE, SensationPro.PLAN_TYPE];
            const keptPlanType = this.baseProductService.getPlanType();
            if (sensationPlans.includes(keptPlanType) && device.isEdpAppliable(this.scheme, keptPlanType)) {
                this.scheme.add(Catalog.getInstance(Edp.defaultData, this.scheme.productContext));
            }
            edp = this.scheme.getProductByType(Edp) as Edp;
        }

        this.scheme.addConfigurators(configurators, edp);
        this.setPromos();
        this.schemeAndPricesSubject.next({ scheme: this.scheme, prices: this.prices });
    }

    public initConfigurators(): void {
        const configurators = [];

        const plan = this.scheme.getProductByType(Plan) as Plan;

        if (!plan) {
            this.configuratorsSubject.next(configurators);
            return;
        }

        const isConnected = !!this.customerContextService?.idPersonne;

        const promosPorta =
            this.promotionService.getPromotionsByTypeMarketingFromAPI(
                TYPE_MARKETING.PORTABILITE,
                true,
                this.apiPlan?.promotions,
            ) || [];
        const promosPortaAmount = this.promotionService.getPromotionsAmount(promosPorta) || 0;

        const urlParams = new URLSearchParams(window.location.search);

        const urlPorta = urlParams.get('portabilite');
        const urlBboxClient = urlParams.get('clientbbox');

        const forcedPorta = urlPorta !== null;
        const forcedBboxClient = urlBboxClient !== null;

        const promosConvergence =
            this.promotionService.getPromotionsByTypeMarketingFromAPI(
                TYPE_MARKETING.CONVERGENCE,
                true,
                this.apiPlan?.promotions,
            ) || [];
        const promosConvergenceAppliable =
            this.promotionService.getPromotionsByTypeMarketingFromAPI(
                TYPE_MARKETING.CONVERGENCE,
                false,
                this.apiPlan?.promotions,
            ) || [];
        const promosConvergenceAmount = this.promotionService.getPromotionsAmount(promosConvergence) || 0;

        const { storedConfiguratorConvergence, storedProtabiltyValue } = this.getStoredConfiguratorValues();
        const canActivateConvergenceConnected = promosConvergenceAppliable.length > 0;
        const presetConfiguratorConvergence = forcedBboxClient
            ? urlBboxClient !== 'false'
            : storedConfiguratorConvergence;
        const convergenceValue = isConnected ? canActivateConvergenceConnected : presetConfiguratorConvergence;
        const presetConfiguratorPorta = forcedPorta ? urlPorta !== 'false' : storedProtabiltyValue;

        if (!this.baseProductService.isRenewal) {
            configurators.push({
                id: ConfiguratorType.CONVERGENCE,
                title: 'Et si je suis déjà client Bbox ?',
                label: this.getPromoConvergenceLabel(promosConvergenceAmount),
                value: convergenceValue,
                enabled: !isConnected,
                show: promosConvergence.length > 0,
                info: 'zone_promos-client-box',
                price: promosConvergenceAmount,
                htmlId: 'already-client-zone',
                values: [
                    {
                        value: true,
                        label: 'Oui',
                    },
                    {
                        value: false,
                        label: 'Non',
                    },
                ],
                accessType: ACCESS_TYPE.CONVERGENCE,
            });

            configurators.push({
                id: ConfiguratorType.PORTA,
                title: 'Et si je veux conserver mon numéro ?',
                label: this.getPromoPortaLabel(promosPortaAmount),
                value: presetConfiguratorPorta,
                enabled: true,
                show: promosPorta.length > 0,
                price: promosPortaAmount,
                htmlId: 'portability-zone',
                info: 'zone_promos-conserver-numero',
                values: [
                    {
                        value: true,
                        label: 'Oui',
                    },
                    {
                        value: false,
                        label: 'Non',
                    },
                ],
                accessType: ACCESS_TYPE.PORTABILITE,
            });
        }
        configurators.push({
            id: ConfiguratorType.EDP,
            title: 'Comment puis-je payer mon téléphone ?',
            label: 'Vous avez plusieurs possibilités, selon votre budget !',
            value: true,
            enabled: true,
            htmlId: 'founding-zone',
            show: true,
            values: [
                {
                    value: true,
                    label: 'Oui',
                },
                {
                    value: false,
                    label: 'Non',
                },
            ],
        });

        configurators.forEach((configurator) => {
            this.scheme.addConfigurators([configurator]);
        });
        this.configuratorsSubject.next(configurators);
    }

    public addToProductAlert(mailObject: any) {
        return this.http.post(this.config.getData()?.global?.catalog.productalert, {}, { params: mailObject });
    }

    public getPromoConvergenceLabel(amount: number): string {
        const isWithoutTax = this.isWithoutTax();
        const numberFix = isWithoutTax ? 2 : 0;
        const value = isWithoutTax ? this.btlHelper.round(amount, numberFix) : amount.toString();
        const labelPorta: string = `d'une remise de <span class="has-text-weight-semibold">-${value}€${
            isWithoutTax ? ' HT' : ''
        }/mois</span>`;
        const labelNoPorta: string = `de prix exclusifs`;

        return `Bonne nouvelle ! Je bénéficie ${amount > 0 ? labelPorta : labelNoPorta} sur mon forfait.`;
    }

    public getPromoPortaLabel(amount: number): string {
        const isWithoutTax = this.isWithoutTax();
        const numberFix = isWithoutTax ? 2 : 0;
        const value = isWithoutTax ? this.btlHelper.round(amount, numberFix) : amount.toString();
        const labelPorta: string = `une remise automatique de <span class="has-text-weight-semibold">-${value}€${
            isWithoutTax ? ' HT' : ''
        }/mois</span>`;
        const labelNoPorta: string = `des prix exclusifs`;

        return `C'est possible, avec en plus, ${
            amount > 0 ? labelPorta : labelNoPorta
        } pendant 6 mois sur mon forfait.`;
    }

    public isProContext(): boolean {
        const isPro =
            this.cartHelper.isPro() ||
            this.customerContextService.isEnterprise() ||
            this.document.defaultView.location.href.includes('telephones-mobiles-pro') ||
            this.document.defaultView.location.href.includes('forfaits-mobiles-pro') ||
            this.document.defaultView.location.href.includes('hotspot-tablette-pro');
        const renewalOrAcc = RegExp(/accessoires|bons-plans/).test(this.document.defaultView.location.href);

        return !renewalOrAcc && isPro;
    }

    public checkWithoutTax(): void {
        this.canApplyWithoutTaxSignal.set(this.isWithoutTax());
    }

    public isWithoutTax(): boolean {
        return this.config.getData()?.global?.belmondo_price_without_tax && this.isProContext();
    }

    public isHidingLearnMoreBtn(): boolean {
        return this.customerContextService.isEnterprise() && !this.cartHelper.isPro();
    }

    public openModal(phoneName: string, modalService: ModalService): void {
        const modalRef: ModalRef<InfoHuaweiModalComponent> = modalService.open<InfoHuaweiModalComponent>(
            InfoHuaweiModalComponent,
            { modalSize: 'small', closeActive: false, backdropClickActive: false },
        );
        const infoModalComponent: InfoHuaweiModalComponent = modalRef.componentInstance;
        infoModalComponent.phoneName = phoneName;
    }

    public closeModal(modalService: ModalService) {
        if (modalService && modalService.modalComponentRef && modalService.modalComponentRef.instance) {
            modalService.modalComponentRef.instance.closeClicked();
        }
    }

    public setRenewMinPlanData(value: JsonProduct) {
        this.renewMinPlanData = value;
    }

    private findMinPlanInResponse(gencode: string, response: ApiProduct[]): Parcours {
        return response.filter((el) => el.gencode === gencode)?.[0].panierSimule?.parcours[0];
    }

    private getPlanTypeToDisplay(): string[] {
        let planType = this.baseProductService.getPlanType();

        const currentPlan = this.cartHelper.getPlan();
        if (currentPlan?.banking_offer && planType === Sowo.PLAN_TYPE) {
            planType = Prompto.PLAN_TYPE;
        } else if (planType === Sowo.PLAN_TYPE) {
            planType = 'plan_sowo_specials';
        }

        return [planType];
    }

    private getStoredConfiguratorValues() {
        let storedConfigurator: { [key: string]: any } = this.cartHelper.getConfigurator();
        storedConfigurator = Object.keys(storedConfigurator).length > 0 ? storedConfigurator : null;
        const storedConvergenceValue =
            typeof storedConfigurator?.convergence !== 'undefined' ? storedConfigurator.convergence : true;
        const storedProtabiltyValue =
            typeof storedConfigurator?.portability !== 'undefined' ? storedConfigurator.portability : true;
        const storedConfiguratorConvergence = !storedConfigurator || storedConvergenceValue;
        return { storedConfiguratorConvergence, storedProtabiltyValue };
    }

    public updateGalliumPrices(planApi: ApiProduct): void {
        const newGalliumPrice = this.storage.getItem(this.storage.LS_KEY.GALLIUM_PRICES);
        newGalliumPrice[planApi?.gencode].enveloppeData = planApi?.libelleEnveloppeData;
        newGalliumPrice[planApi?.gencode].libelleRenouvellement = planApi?.nom;
        newGalliumPrice[planApi?.gencode].connectivite = planApi?.connectivite;
        if (planApi?.prix.initial !== planApi?.prix.final) {
            newGalliumPrice[planApi?.gencode].price = planApi?.prix.initial;
            newGalliumPrice[planApi?.gencode].promoPrice = planApi?.prix.final;
        } else {
            newGalliumPrice[planApi?.gencode].price = planApi?.prix.final;
        }
        this.storage.setItem(this.storage.LS_KEY.GALLIUM_PRICES, newGalliumPrice);
    }
}
